import * as React from "react"
import { Tab, Row, Col, Modal, Nav, Spinner } from "react-bootstrap"
import { Subscription } from "rxjs"
import { toast } from "react-toastify"
import ClinicSearchListType from "../../api/Clinics/Models/ClinicSearchListType"
import { ClinicSearchListTypesGetObservable } from "../../api/Clinics/ClinicOptionsGet"
import DefaultOptions from "./ClinicSearchOptions/DefaultOptions"
import ListOptions from "./ClinicSearchOptions/ListOptions"
import { AppState } from "../../AppState"
import { isCompanyAdmin, isPortalAdmin } from "../../authentication/isAdminRole"
import { connect } from "react-redux"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { RouteComponentProps } from "react-router"
import { Home } from "../../config/Routes"
import ExcludeClinicGroup from "./ClinicSearchOptions/ExcludeClinicGroup/ExcludeClinicGroup"

enum TabName {
  DEFAULT = "Default", 
  BLACKLIST = "Blacklist"
} 

interface ListData {
  name: string
  title: string
  tabName: string
  headerText?: string
  clinicSearchListTypeID: number
  visible: (state: ClinicSearchOptionsState) => boolean
}

const LISTS: ListData[] = [
  {
    name: "Primary",
    title: "Primary",
    tabName: "Primary",
    clinicSearchListTypeID: 0,
    visible: (state) => state.isPortal || state.isCompany
  },
  {
    name: TabName.BLACKLIST,
    title: "Exclusion List",
    tabName: "Exclusion List",
    clinicSearchListTypeID: 0,
    headerText: "Exclude by individual FormFox Site",
    visible: (state) => state.isPortal || state.isCompany,
  },
  {
    name: "Favorite",
    title: "Favorite",
    tabName: "Favorites",
    clinicSearchListTypeID: 0,
    visible: (state) => false
  }
]

interface ClinicSearchOptionsProps {
  appState: AppState
}

interface ClinicSearchOptionsState {
  currentTab: string
  loading: boolean
  lists: ListData[]
  isPortal: boolean
  isCompany: boolean
}

export class ClinicSearchOptions extends React.PureComponent<ClinicSearchOptionsProps & RouteComponentProps, ClinicSearchOptionsState> {
  state: ClinicSearchOptionsState = {
    currentTab: TabName.DEFAULT,
    loading: false,
    lists: [],
    isPortal: isPortalAdmin(this.props.appState),
    isCompany: isCompanyAdmin(this.props.appState)
  }

  loadSubscription?: Subscription

  componentDidMount() {
    if (this.state.isPortal || this.state.isCompany) {
      this.setState({ loading: true })
      this.loadSubscription = ClinicSearchListTypesGetObservable().subscribe(
        data => this.setState({ lists: this.buildVisibleLists(data), loading: false }),
        (error) => toast.error(error.message),
        () => this.setState({ loading: false })
      )
    }
  }

  componentWillUnmount() {
    this.loadSubscription && this.loadSubscription.unsubscribe()
  }

  buildVisibleLists = (listTypes: ClinicSearchListType[]): ListData[] => {
    const lists = LISTS.map(item => ({
      ...item,
      clinicSearchListTypeID: listTypes.find(listType => listType.Name === item.name)?.ClinicSearchListTypeID || 0
    }))
    return lists.filter(item => item.clinicSearchListTypeID > 0 && item.visible(this.state))
  }

  handleCancel = () => (this.props as RouteComponentProps).history?.push(Home)

  render() {
    const { currentTab, loading, lists, isPortal, isCompany } = this.state
    return (
      <AppPageWrapper showNav>
        <Modal.Dialog size="xl" >
          <Modal.Header>
            <Modal.Title>
              Clinic Search Options/{lists.find((item) => item.name === currentTab)?.title || TabName.DEFAULT}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="pt-0">
            <Tab.Container defaultActiveKey={TabName.DEFAULT}>
              <Row>
                <Col lg={2} className="pt-2">
                  <Nav
                    variant="pills"
                    className="flex-column pills-sm"
                    onSelect={(eventKey) => {
                      eventKey && this.setState({ currentTab: eventKey })
                    }}
                  >
                    <Nav.Item>
                      <Nav.Link eventKey={TabName.DEFAULT}>Default</Nav.Link>
                    </Nav.Item>
                    {lists.map((item) => (
                      <Nav.Item key={item.name}>
                        <Nav.Link eventKey={item.name}>{item.tabName}</Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                  {loading && (
                    <div className="text-center">
                      <Spinner animation="border" variant="secondary" size="sm" />
                    </div>
                  )}
                  <em hidden={TabName.DEFAULT === currentTab} className="small">
                    To add, enter clinic information and click "Site Search" and you will be presented with a list site
                    matching search criteria. Locate the desired site and click "Select Site" to add.
                    <br />
                    <br />
                    To remove, highlight record and click "Remove".
                  </em>
                </Col>
                <Col lg={10}>
                  <Tab.Content>
                    <Tab.Pane eventKey={TabName.DEFAULT}>
                      {TabName.DEFAULT === currentTab && (
                        <DefaultOptions onCancel={() => this.handleCancel()} isPortal={isPortal} />
                      )}
                    </Tab.Pane>
                    {!loading &&
                      lists.map((item) => (
                        <Tab.Pane key={item.name} eventKey={item.name} title={item.tabName}>
                          {item.name === currentTab && TabName.BLACKLIST === currentTab && isPortal && <ExcludeClinicGroup />}
                          {item.name === currentTab && (
                            <ListOptions
                              isCompany={isCompany}
                              IsPrimary={item.tabName === "Primary"}
                              headerText={item.headerText}
                              clinicSearchListTypeID={item.clinicSearchListTypeID}
                              onCancel={() => this.handleCancel()}
                            />
                          )}
                        </Tab.Pane>
                      ))}
                  </Tab.Content>
                </Col>
              </Row>
            </Tab.Container>
          </Modal.Body>
        </Modal.Dialog>
      </AppPageWrapper>
    )
  }
}

function mapStateToProps(state: AppState): { appState: AppState } {
  return {
    appState: state
  }
}
const ConnectedClinicSearchOptions = connect(mapStateToProps, null)(ClinicSearchOptions)
export default ConnectedClinicSearchOptions